import { computed, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { useTranslation } from '@centric-os/helpers';
import { SitesLocalePrefix } from '../../../enums';
import { useRecommendationsStore } from '../../../routes/stores/use-recommendations-store';
import { storeToRefs } from 'pinia';
import type { PagePanel } from '@centric-os/types';
import Recommendations from '../../../routes/site-details/components/overview/recommendations/Recommendations.vue';
import RecommendationNavigation from '../../../routes/site-details/components/overview/recommendations/nav/RecommendationNavigation.vue';

export function useRecommendationsPanel(options?: {
  hideNavigation?: boolean;
  hideDetailsButton?: boolean;
}): {
  recommendationsPanel: ComputedRef<PagePanel>;
} {
  const route = useRoute();
  const { translate } = useTranslation();
  const LOCALE_PREFIX = SitesLocalePrefix.SITE_OVERVIEW;

  const recommendationsStore = useRecommendationsStore();
  const { currentTimeframeSubfilter, hasError: recommendationHasError } =
    storeToRefs(recommendationsStore);

  const recommendationsPanelSubtitle = computed<string>(() => {
    if (recommendationHasError.value)
      return translate(`${LOCALE_PREFIX}.recommendations.brokenRecommendationsSubtitle`);
    if (recommendationsStore.recommendations.length === 0)
      return translate(`${LOCALE_PREFIX}.recommendations.noRecommendationsSubtitle`);

    return currentTimeframeSubfilter.value;
  });

  const recommendationsPanel = computed<PagePanel>(() => ({
    header: translate(`${LOCALE_PREFIX}.recommendations.title`),
    headerSubtitle: recommendationsPanelSubtitle.value,
    component: Recommendations,
    props: {
      siteId: route.params.siteId as string,
      hideDetailsButton: options?.hideDetailsButton,
    },
    actionsComponent: options?.hideNavigation ? undefined : RecommendationNavigation,
  }));

  return {
    recommendationsPanel,
  };
}
